/* eslint-disable no-restricted-syntax */
import {createAsyncThunk, createSlice} from '@reduxjs/toolkit'
import {http} from 'hiya-common/src/utils/axios'
import {useSelector} from 'react-redux'
import _forEach from 'lodash/forEach'
import _isEmpty from 'lodash/isEmpty'

export const transformDelegateInfo = (it) => {
  if (_isEmpty(it)) {
    return {}
  }
  const privileges = []
  _forEach(it?.privileges, (item) => {
    _forEach(item?.medals || item?.ra_medal_list, (v) => {
      const url = v.icon || v.dmk_icon || v.icon_v2
      if (url) {
        privileges.push(url)
      }
    })
  })
  return {
    privileges,
    country: it?.flag?.url,
    id: it?.mid,
    is_safe: it?.is_safe,
    channels: it?.channels || [],
    name: it?.member?.name,
    avatar: it?.member?.avatar_urls?.aspect_low?.urls?.[0],
  }
}

/** @deprecated use transformDelegateInfo */
export const transformInfo = transformDelegateInfo

const transformBoundClient = (bound_to_mid, bound) => {
  if (!bound_to_mid || _isEmpty(bound)) {
    return {}
  }
  return transformDelegateInfo({
    mid: bound?.mid ?? bound_to_mid,
    member: bound?.member || bound,
    is_safe: bound?.is_safe,
    channels: bound?.channels,
    privileges: bound?.privileges,
    flag: bound?.flag,
  })
}

const parseChannelTemplateFields = (fields) => {
  if (!fields) {
    return []
  }
  if (Array.isArray(fields)) {
    return fields
  }
  if (typeof fields !== 'string') {
    return []
  }
  try {
    const parsed = JSON.parse(fields)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

const normalizePayoutChannelData = (data) => {
  const channel_account_cfgs = (data?.channel_templates || []).map((tpl) => {
    return {
      id: tpl.id,
      channel: tpl.channel,
      icon: tpl.icon,
      fields: parseChannelTemplateFields(tpl.fields),
    }
  })
  return {
    err_msg: data?.err_msg,
    channel_account_cfgs,
    list: data?.user_accounts || [],
  }
}

// http://wiki.xunlei.cn/pages/viewpage.action?pageId=189208737 §5.6
export const get_saada_merchant_pearl_delegate_list = createAsyncThunk(
  'saada_merchant_collection/get_saada_merchant_pearl_delegate_list',
  async (p: any, store: any) => {
    const resp = await http.post(
      '/live/saada_settle/get_saada_merchant_pearl_delegate_list',
      {...p},
      {store, xcErrorToast: true}
    )
    return resp.data.data
  }
)

// §5.7
export const bind_saada_merchant_pearl_delegate = createAsyncThunk(
  'saada_merchant_collection/bind_saada_merchant_pearl_delegate',
  async (p: {to_mid: number}, store: any) => {
    const resp = await http.post(
      '/live/saada_settle/bind_saada_merchant_pearl_delegate',
      {...p},
      {store, xcErrorToast: true}
    )
    return resp.data.data
  }
)

// §5.8
export const get_saada_merchant_pearl_payout_channel_list = createAsyncThunk(
  'saada_merchant_collection/get_saada_merchant_pearl_payout_channel_list',
  async (p: any, store: any) => {
    const resp = await http.post(
      '/live/saada_settle/get_saada_merchant_pearl_payout_channel_list',
      {...p},
      {store, xcErrorToast: true}
    )
    return normalizePayoutChannelData(resp.data.data)
  }
)

// §5.9
export const update_saada_merchant_pearl_payout_account = createAsyncThunk(
  'saada_merchant_collection/update_saada_merchant_pearl_payout_account',
  async (p: {action: 1 | 2 | 3; account?: any}, store: any) => {
    const resp = await http.post(
      '/live/saada_settle/update_saada_merchant_pearl_payout_account',
      {...p},
      {store, xcErrorToast: true}
    )
    return resp.data.data
  }
)

// §5.10
export const set_saada_merchant_pearl_payout_account_default = createAsyncThunk(
  'saada_merchant_collection/set_saada_merchant_pearl_payout_account_default',
  async (p: {account_id: string | number}, store: any) => {
    const resp = await http.post(
      '/live/saada_settle/set_saada_merchant_pearl_payout_account_default',
      {...p},
      {store, xcErrorToast: true}
    )
    return resp.data.data
  }
)

// §5.12 http://wiki.xunlei.cn/pages/viewpage.action?pageId=189208737
export const get_saada_merchant_pearl_exchange_ratio = createAsyncThunk(
  'saada_merchant_collection/get_saada_merchant_pearl_exchange_ratio',
  async (p: any, store: any) => {
    const resp = await http.post(
      '/live/saada_settle/get_saada_merchant_pearl_exchange_ratio',
      {...p},
      {store, xcErrorToast: true}
    )
    return resp.data.data
  }
)

// §5.13
export const handle_saada_merchant_pearl_order_from_app = createAsyncThunk(
  'saada_merchant_collection/handle_saada_merchant_pearl_order_from_app',
  async (p: any, store: any) => {
    const resp = await http.post(
      '/live/saada_settle/handle_saada_merchant_pearl_order_from_app',
      {...p},
      {store, xcErrorToast: true}
    )
    return resp.data.data
  }
)

// §5.14
export const handle_dispute_saada_merchant_pearl_order_event = createAsyncThunk(
  'saada_merchant_collection/handle_dispute_saada_merchant_pearl_order_event',
  async (p: any, store: any) => {
    const resp = await http.post(
      '/live/saada_settle/handle_dispute_saada_merchant_pearl_order_event',
      {...p},
      {store, xcErrorToast: true}
    )
    return resp.data.data
  }
)

export interface InitialStateType {
  client: any
  bound_to_mid?: number
  payment?: {
    channel_account_cfgs?: {
      id: number
      channel: string
      icon: string
      fields: {
        key: string
        value: string
        isRequired: boolean
      }[]
    }[]
    list?: any[]
  }
}

const initialState: InitialStateType = {
  client: {},
  bound_to_mid: undefined,
}

export const CoinAgencyCollectSalarySlice = createSlice({
  name: 'Animal_saada_m_collection',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(
      get_saada_merchant_pearl_delegate_list.fulfilled,
      (state, action) => {
        const {bound_to_mid, bound_member, list} = action.payload || {}
        // eslint-disable-next-line no-param-reassign
        state.bound_to_mid = bound_to_mid
        if (bound_to_mid) {
          const boundDelegate =
            list?.find((item) => item?.mid === bound_to_mid) || bound_member
          // eslint-disable-next-line no-param-reassign
          state.client = transformBoundClient(bound_to_mid, boundDelegate)
        }
      }
    )
    builder.addCase(
      get_saada_merchant_pearl_payout_channel_list.fulfilled,
      (state, action) => {
        Object.assign(state, {
          payment: {
            ...state.payment,
            ...action.payload,
          },
        })
      }
    )
  },
})
export default CoinAgencyCollectSalarySlice.reducer
export const CoinAgencyCollectSalaryActions =
  CoinAgencyCollectSalarySlice.actions

export const useSaadaMerchantCollectionState = () => {
  const data = useSelector((state) => {
    return state.Animal_saada_m_collection
  })
  return data
}
